
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Calendar, User } from 'lucide-react';
import { useSubstackFeed } from '@/hooks/useSubstackFeed';
import { Skeleton } from '@/components/ui/skeleton';

const SubstackFeed: React.FC = () => {
  const { data: posts, isLoading, error, refetch } = useSubstackFeed(10);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Latest Posts</h2>
          <p className="text-muted-foreground mb-6">Stay updated with our latest insights and resources</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Latest Posts</h2>
          <p className="text-muted-foreground mb-6">Stay updated with our latest insights and resources</p>
        </div>
        
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">Failed to load posts. Please try again later.</p>
          <div className="flex justify-center gap-4">
            <Button 
              onClick={() => refetch()}
              variant="default"
            >
              Retry Loading
            </Button>
            <Button 
              onClick={() => window.open('https://getprompts.substack.com', '_blank')}
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Visit Substack
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Latest Posts</h2>
          <p className="text-muted-foreground mb-6">Stay updated with our latest insights and resources</p>
        </div>
        
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">No posts available at the moment.</p>
          <div className="flex justify-center gap-4">
            <Button 
              onClick={() => refetch()}
              variant="default"
            >
              Refresh Posts
            </Button>
            <Button 
              onClick={() => window.open('https://getprompts.substack.com', '_blank')}
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Visit Substack
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Debug titles in console
  console.log('Rendering posts with titles:', posts.map(post => post.title));

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Latest Posts</h2>
        <p className="text-muted-foreground mb-6">Stay updated with our latest insights and resources</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Card key={post.id} className="h-full flex flex-col hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-lg">📰</span>
                <Badge variant="outline" className="text-xs">
                  Blog Post
                </Badge>
              </div>
              
              <CardTitle className="text-lg leading-tight font-semibold">
                <div className="line-clamp-2">
                  {post.title || 'Substack Article'}
                </div>
              </CardTitle>
              
              {post.author && (
                <p className="text-sm text-muted-foreground truncate">by {post.author}</p>
              )}
            </CardHeader>
            
            <CardContent className="pt-0 flex-1 flex flex-col">
              {post.description && (
                <CardDescription className="flex-1 mb-4">
                  <div className="line-clamp-3">
                    {post.description}
                  </div>
                </CardDescription>
              )}
              
              <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(post.published_at)}</span>
                </div>
                {post.author && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{post.author}</span>
                  </div>
                )}
              </div>
              
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {post.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {post.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{post.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
              
              <div className="mt-auto">
                <Button 
                  className="w-full" 
                  onClick={() => window.open(post.url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Read Article
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="text-center pt-6">
        <Button 
          onClick={() => window.open('https://getprompts.substack.com', '_blank')}
          variant="outline"
          size="lg"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View All Posts
        </Button>
      </div>
    </div>
  );
};

export default SubstackFeed;
